import { UIIcon } from '../config/icons';
import { CBookingId, cBookingPartConfirm, cBookingPartReject, cBookingRate, cBookingReview } from '../lib/mdl/c-booking';
import { cVideoViewAction } from '../lib/mdl/c-video-home';
import { MInboxCustomItem, MInboxRateEventItem, MInboxStandbyReservedItem } from '../lib/mdl/m-inbox';
import { SObjectId } from '../lib/mdl/s-object';
import { meInboxSeen } from '../lib/me/inbox';
import { the_user } from '../lib/me/login';
import { meOnce } from '../lib/me/preferences';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UIImageView } from '../lib/ui/component/image';
import { UITextInput } from '../lib/ui/component/input';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { UIRelPos } from '../lib/ui/types';
import { $analytics } from '../lib/y/analytics';
import { $i18n } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $time } from '../lib/y/time';
import { YId } from '../lib/y/types';
import { homeDisplayCantBookMessage } from './event';

const tz = 'Europe/Stockholm';

export class HomeReviewEventDialog extends UIDialog {
    constructor(booking_id: CBookingId | undefined, video_view_id: YId | undefined, picture: SObjectId) {
        super({ close: true, classes: 'm-440 m-join-top' });

        $analytics.track('Review pop up');

        const text = new UITextInput({ lines: 3 });

        this.content.ui_add([
            new UIImageView({ media: picture }, { classes: 'tl-dialog-top-image' }),
            $el.DIV('w-block l-form m-center blk-inbox', [
                $el.H3('heading', $i18n.tl.b_leave_comment),
                text,
                new UIButton({
                    title: $i18n.tl.b_send, classes: 'ui-tint-reverse', on_click: () => {
                        const value = text.value;
                        if(value) {
                            if(booking_id)
                                return cBookingReview(booking_id, text.value).then(() => { this.dismiss(true) });
                            if(video_view_id)
                                return cVideoViewAction(video_view_id, 'review', { text: text.value }).then(() => { this.dismiss(true) });
                        }
                        return;
                    }
                }),
                new UIButton({ kind: 'k-text', classes: 'ui-tint-reverse m-small g-0', title: $i18n.tl.b_not_now, on_click: () => { this.dismiss(true) } })
            ])
        ]);
    }
}

class RateEventDialog extends UIDialog {
    constructor(item: MInboxRateEventItem) {
        super({ close: true, classes: 'm-440 m-join-top' });

        $analytics.track('Rating pop up');

        let chosen = false;

        function choose(what: 'up' | 'down') {
            if(chosen) return;
            chosen = true;
            thumbs.classList.add('f-' + what);
            block.classList.add('f-chosen');
            not_now.title = $i18n.tl.b_close;
            if(item.data.booking_id)
                cBookingRate(item.data.booking_id, what);
            else if(item.data.video_view_id)
                cVideoViewAction(item.data.video_view_id, 'rate', { rating: what });
        }

        const thumb_up = new UIButton({ kind: 'k-square', classes: 'm-round m-hollow', icon: UIIcon.THUMBS_UP, on_click: () => { choose('up') } });
        const thumb_down = new UIButton({ kind: 'k-square', classes: 'm-round m-hollow', icon: UIIcon.THUMBS_DOWN, on_click: () => { choose('down') } });

        const thumbs = $el.DIV('tl-inbox-thumbs', [
            $el.DIV('r-up', thumb_up),
            $el.DIV('r-down', thumb_down),
            $el.DIV('r-after', [
                $el.DIV('r-thanks', $i18n.tl.h_thanks),
                new UIButton({
                    kind: 'k-text', classes: 'r-comment ui-tint-reverse', title: $i18n.tl.b_leave_comment, on_click: () => {
                        this.dismiss(new HomeReviewEventDialog(item.data.booking_id, item.data.video_view_id, item.data.event_picture).present({ replace: this }));
                    }
                })
            ])
        ])

        const not_now = new UIButton({
            kind: 'k-text', classes: 'ui-tint-reverse m-small g-0', title: $i18n.tl.b_not_now, on_click: () => {
                if(!chosen) {
                    $analytics.track('Rating dismissed');

                    if(meOnce('rate_event_message_shown')) {
                        showWarning({ text: $i18n.tl.w_dismiss_rating, cancel_text: null });
                    }
                }

                this.dismiss(true)
            }
        });

        const block = $el.DIV('w-block l-form m-center blk-inbox', [
            $el.P('subheading', [$i18n.tl.t_rate_event + ' ', $el.SPAN('t-w-bold', item.data.event_name), '?'], 'r-question'),
            thumbs,
            not_now
        ]);

        this.content.ui_add([
            new UIImageView({ media: item.data.event_picture }, { classes: 'tl-dialog-top-image' }),
            block
        ])
    }
}

class StandbyReservedDialog extends UIDialog {
    constructor(item: MInboxStandbyReservedItem) {
        super({ close: true, classes: 'm-440 m-join-top' });

        $analytics.track('Standby pop up');

        const message = $el.DIV('r-message', $i18n.tl.h_booking_confirmation);
        const not_now = new UIButton({ kind: 'k-text', classes: 'm-small g-0', title: $i18n.tl.b_not_now, on_click: () => { this.dismiss(null) } })
        const info = $el.ICON(UIIcon.CIRCLE_INFO | UIIcon.WEIGHT_SOLID);
        addTooltip($i18n.tl.tt_accept_latest + ' ' + $time(item.data.booking_expires).formatIn('EEE d MMM HH:mm', tz), this,
            { offset: 8, hover: info, anchor: info, position: UIRelPos.Above });

        const block = $el.DIV('w-block l-form m-center blk-inbox', [
            $el.H3('heading', [$i18n.tl.t_have_reservation + ' ', info]),
            $el.P('info', item.data.event_name),
            $el.DIV('tl-time-or-place', $time(item.data.session_time).format('dd MMM HH:mm')),
            $el.DIV('s-medium'),
            $el.DIV('l-button-row', [
                message,
                new UIButton({
                    title: $i18n.tl.b_accept, classes: 'ui-tint-reverse', on_click: () => {
                        cBookingPartConfirm(item.data.booking_part_id).then(() => {
                            block.classList.add('f-chosen');
                            $location.reload();
                            not_now.title = $i18n.tl.b_close;
                        }, reason => {
                            return homeDisplayCantBookMessage(reason);
                        });
                    }
                }),
                new UIButton({
                    title: $i18n.tl.b_decline, classes: 'm-hollow ui-tint-reverse', on_click: () => {
                        showWarning({ text: $i18n.tl.t_decline_confirmation, cancel_text: $i18n.tl.b_cancel, ok_text: $i18n.tl.b_confirm }).then(() => {
                            cBookingPartReject(item.data.booking_part_id).then(() => {
                                message.textContent = $i18n.tl.s_reservation_declined;
                                $analytics.track('Standby Ticket Declined');
                                block.classList.add('f-chosen');
                                $location.reload();
                                not_now.title = $i18n.tl.b_close;
                            });
                        });
                    }
                })
            ]),
            not_now
        ])

        this.content.ui_add([
            new UIImageView({ media: item.data.event_picture }, { classes: 'tl-dialog-top-image' }),
            block
        ]);
    }
}

class CustomDialog extends UIDialog {
    constructor(item: MInboxCustomItem) {
        super({ close: true, classes: 'm-550' });

        $analytics.track('Custom pop up');

        const block = $el.DIV('w-block l-form m-center blk-inbox m-custom');
        block.innerHTML = item.data.content;

        this.content.ui_add([
            block
        ]);
    }
}

let inbox_shown = false;

export function homeShowInbox() {
    if(!the_user || inbox_shown) return;
    const inbox = the_user.inbox;
    if(inbox) {
        if(!inbox.length) return;
        inbox_shown = true;
        for(const item of inbox) {
            if(item.seen_at || $time(item.publish_at).is_future) continue;

            if(item.kind == 'rate_event') {
                new RateEventDialog(item).present();
            } else if(item.kind == 'standby_reserved') {
                if(item.data.booking_part_id)
                    new StandbyReservedDialog(item).present();
            } else if(item.kind == 'custom') {
                new CustomDialog(item).present();
            } else {
                continue;
            }

            item.seen_at = $time.now().toJSON();
            meInboxSeen(item.id);

            break;
        }
        return;
    }
}
